// src/pages/courtowner/BlockSlotsSingle.js
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import CourtOwnerSidebar from "../../components/courtowner/CourtOwnerSidebar";

const API_BASE = "https://api.getfitindia.in/api";

const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
};

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

function BlockSlotsSingle() {
  const navigate = useNavigate();

  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState("");
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState("");

  const [availableSlots, setAvailableSlots] = useState([]);

  // form
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [reason, setReason] = useState("");

  // slots to send in POST
  const [slotsToBlock, setSlotsToBlock] = useState([]);

  // already blocked (for info list)
  const [blockedSlots, setBlockedSlots] = useState([]);

  const getAuthHeader = () => {
    const token =
      localStorage.getItem("courtOwnerToken") || localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  /* ========== Load Venues ========== */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/courtowner/venues`, {
          headers: { ...getAuthHeader() },
        });
        if (!res.ok) throw new Error("Failed to load venues");
        const data = await res.json();
        setVenues(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        alert("Failed to load venues");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ========== Load Courts when venue changes ========== */
  useEffect(() => {
    if (!selectedVenue) {
      setCourts([]);
      setSelectedCourt("");
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/slots/${selectedVenue}/courts`, {
          headers: { ...getAuthHeader() },
        });
        if (!res.ok) throw new Error("Failed to load courts");
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.courts || [];
        setCourts(list);
      } catch (e) {
        console.error(e);
        alert("Failed to load courts");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue]);

  /* ========== Load Available Slots (from ManageSlots) ========== */
  useEffect(() => {
    if (!selectedVenue || !selectedCourt) {
      setAvailableSlots([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/slots/${selectedVenue}/courts`, {
          headers: { ...getAuthHeader() },
        });
        if (!res.ok) throw new Error("Failed to load slots");
        const data = await res.json();
        const court = Array.isArray(data)
          ? data.find((c) => c._id === selectedCourt)
          : data.courts?.find((c) => c._id === selectedCourt);
        setAvailableSlots(court?.slots || []);
      } catch (e) {
        console.error(e);
        alert("Failed to load slots");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourt, selectedVenue]);

  /* ========== Load already blocked slots for this court & date ========== */
  useEffect(() => {
    if (!selectedCourt || !date) {
      setBlockedSlots([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/courtowner/blockslots/${selectedCourt}?date=${date}`,
          { headers: { ...getAuthHeader() } }
        );
        if (!res.ok) throw new Error("Failed to fetch blocked slots");
        const data = await res.json();
        setBlockedSlots(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        alert("Failed to load blocked slots");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourt, date]);

  /* ========== Helpers ========== */

  const selectedCourtObj =
    courts.find((c) => c._id === selectedCourt) || null;
  const selectedCourtName =
    selectedCourtObj?.courtName ||
    selectedCourtObj?.name ||
    "Selected Court";
const blockedStartTimes = useMemo(() => {
  return blockedSlots.map(b => b.startTime);
}, [blockedSlots]);

  // Start times come from availableSlots
const startTimeOptions = useMemo(() => {
  const unique = Array.from(
    new Set(
      availableSlots
        .filter(s => !blockedStartTimes.includes(s.startTime)) // ⛔ remove blocked slots
        .map((s) => s.startTime)
    )
  );
  return unique.sort();
}, [availableSlots, blockedStartTimes]);


  // When startTime changes, auto pick endTime from corresponding slot
  useEffect(() => {
    if (!startTime) {
      setEndTime("");
      return;
    }
    const slot = availableSlots.find((s) => s.startTime === startTime);
    setEndTime(slot?.endTime || "");
  }, [startTime, availableSlots]);

  const canAddSlot =
    selectedVenue &&
    selectedCourt &&
    date &&
    startTime &&
    endTime &&
    !!availableSlots.length;

  const totalAmount = useMemo(
    () =>
      slotsToBlock.reduce(
        (sum, s) => sum + Number(s.price || 0),
        0
      ),
    [slotsToBlock]
  );

  /* ========== Add Slot to "Slot Details" ========== */
 const handleAddSlot = () => {
  if (!startTime || !endTime) {
    alert("Please select both start & end time");
    return;
  }

  // Find all 30-min slots between range
  const selectedRange = availableSlots.filter(
  (s) =>
    s.startTime >= startTime &&
    s.endTime <= endTime &&
    !blockedStartTimes.includes(s.startTime)
).map((s) => ({
  date,
  startTime: s.startTime,
  endTime: s.endTime,
  price: s.price ?? 0  // take price directly from ManageSlots-generated content
}));


  if (!selectedRange.length) {
    alert("No valid 30-minute slots in this range");
    return;
  }

 const newSlots = selectedRange; // because already processed above


  setSlotsToBlock((prev) => [
    ...prev.filter(
      (p) => !newSlots.some((n) => n.startTime === p.startTime)
    ),
    ...newSlots,
  ].sort((a, b) => a.startTime.localeCompare(b.startTime)));
};


  const removeSlotFromList = (idx) => {
    setSlotsToBlock((prev) => prev.filter((_, i) => i !== idx));
  };

  /* ========== Block all slots in list ========== */
  const handleBlockSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVenue || !selectedCourt) {
      alert("Please select venue and court");
      return;
    }
    if (!date) {
      alert("Please select date");
      return;
    }
    if (!slotsToBlock.length) {
      alert("Please add at least one slot");
      return;
    }

    const body = {
      venueId: selectedVenue,
      courtId: selectedCourt,
      date,
      reason,
      slots: slotsToBlock.map((s) => ({
        startTime: s.startTime,
        endTime: s.endTime,
        price: s.price,
      })),
    };

    try {
      const res = await fetch(`${API_BASE}/courtowner/blockslots`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to block slots");
      }

      alert("Slot(s) blocked successfully");

      setSlotsToBlock([]);
      setReason("");

      // refresh blocked list
      const refreshed = await fetch(
        `${API_BASE}/courtowner/blockslots/${selectedCourt}?date=${date}`,
        { headers: { ...getAuthHeader() } }
      );
      const updated = await refreshed.json();
      setBlockedSlots(Array.isArray(updated) ? updated : []);
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  /* ========== Unblock one slot ========== */
  const handleUnblock = async (slotId) => {
    if (!window.confirm("Unblock this slot?")) return;
    try {
      const res = await fetch(`${API_BASE}/courtowner/blockslots/${slotId}`, {
        method: "DELETE",
        headers: { ...getAuthHeader() },
      });
      if (!res.ok) throw new Error("Failed to unblock slot");
      setBlockedSlots((prev) => prev.filter((b) => b._id !== slotId));
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };
const handleUnblockAll = async () => {
  if (!blockedSlots.length) return;

  if (
    !window.confirm(
      "Unblock all blocked slots for this court and date?"
    )
  )
    return;

  try {
    await Promise.all(
      blockedSlots.map((b) =>
        fetch(`${API_BASE}/courtowner/blockslots/${b._id}`, {
          method: "DELETE",
          headers: { ...getAuthHeader() },
        })
      )
    );

    setBlockedSlots([]);
  } catch (err) {
    console.error(err);
    alert("Failed to unblock all slots");
  }
};
// =======================helper==================
const TimeDropdown = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      {/* Input Box */}
      <div
        className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm flex items-center cursor-pointer bg-white"
        onClick={() => setOpen(!open)}
      >
        {value ? formatTime(value) : placeholder}
      </div>

      {/* Dropdown list */}
      {open && (
        <div className="absolute mt-1 w-full bg-white shadow-lg rounded-lg border max-h-32 overflow-y-auto z-50">
          {options.map((t) => (
            <div
              key={t}
              className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer"
              onClick={() => {
                onChange(t);
                setOpen(false);
              }}
            >
              {formatTime(t)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

  /* ========== UI ========== */

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="hidden lg:block w-64 bg-white shadow">
        <CourtOwnerSidebar />
      </aside>

      {/* Main */}
      <main className="flex-1 lg:pl-0">
        {/* Top bar */}
      
<div className="bg-[#0f2250] text-white sticky top-0 z-20">
          <div className="max-w-6xl  px-4 py-4 flex items-center gap-2">
            {/* <button
              onClick={() => navigate(-1)}
              className="p-1 rounded hover:bg-white/10"
              aria-label="Back"
            >
              <ChevronLeft />
            </button> */}
            <div className="lg:hidden mt-10">
                          <CourtOwnerSidebar />
                        </div>
            <h1 className="text-lg font-semibold  sm:ms-0 md:ms-1 lg:ms-0 mt-1 ">Block Slots</h1>
          </div>
        </div>
        <div className=" ">
          {/* <div className="mt-4 flex flex-wrap gap-3">
            <NavLink
              to="/courtowner/manageslots"
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium ${
                  isActive
                    ? "bg-[#0f2250] text-white"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`
              }
            >
              Manage Courts & Slots
            </NavLink>
            <NavLink
              to="/courtowner/blockslots"
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium ${
                  isActive
                    ? "bg-[#0f2250] text-white"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`
              }
            >
              Block Slots (Single)
            </NavLink>
            <NavLink
              to="/courtowner/blockslots/bulk"
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium ${
                  isActive
                    ? "bg-[#0f2250] text-white"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`
              }
            >
              Block Slots (Bulk)
            </NavLink>
          </div> */}

          <form
  id="blockSlotForm"
  onSubmit={handleBlockSubmit}
  className="bg-white rounded-2xl  mt-4 p-4 md:p-6 pb-32"
>

            {/* Select property & court */}
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Property
                </label>
                <select
                  value={selectedVenue}
                  onChange={(e) => {
                    setSelectedVenue(e.target.value);
                    setSelectedCourt("");
                    setAvailableSlots([]);
                    setSlotsToBlock([]);
                  }}
                  className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm"
                >
                  <option value="">Select Property</option>
                  {venues.map((v) => (
                    <option key={v._id} value={v._id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Court
                </label>
                <select
                  value={selectedCourt}
                  onChange={(e) => {
                    setSelectedCourt(e.target.value);
                    setSlotsToBlock([]);
                  }}
                  className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm"
                  disabled={!selectedVenue}
                >
                  <option value="">Select Court</option>
                  {courts.map((c, idx) => (
                    <option key={c._id || idx} value={c._id || ""}>
                      {c.courtName || c.name || `Court ${idx + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

<div className="min-[768px]:flex min-[768px]:gap-4">
  
  {/* Reason */}
  <div className="mt-4 min-[768px]:w-1/2">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Reason
    </label>
    <input
      type="text"
      value={reason}
      onChange={(e) => setReason(e.target.value)}
      className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm"
      placeholder="Maintenance, event, etc."
    />
  </div>

  {/* Start Date */}
  <div className="mt-4 min-[768px]:w-1/2">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Start Date
    </label>
    <input
      type="date"
      value={date}
      onChange={(e) => {
        setDate(e.target.value);
        setSlotsToBlock([]);
      }}
      className="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm"
    />
  </div>

</div>


            {/* Start / End time */}
            <div className="grid grid-cols-2 gap-5 mt-4">
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Start Time
    </label>

    <TimeDropdown
      value={startTime}
      onChange={(v) => setStartTime(v)}
      options={startTimeOptions}
      placeholder="Start Time"
    />
  </div>

  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      End Time
    </label>

    <TimeDropdown
      value={endTime}
      onChange={(v) => setEndTime(v)}
      options={availableSlots
        .filter(
          (s) =>
            s.startTime >= startTime &&
            !blockedStartTimes.includes(s.startTime)
        )
        .map((s) => s.endTime)}
      placeholder="End Time"
    />
  </div>
</div>




            {/* Add slot button */}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleAddSlot}
                disabled={!canAddSlot}
                className={`h-9 px-6 rounded-lg text-sm font-semibold ${
                  canAddSlot
                    ? "bg-[#0f2250] text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                ADD SLOT
              </button>
            </div>

            {/* Slot Details card (similar to screenshot) */}
          {/* Slot Details - show ONLY when slot is added */}
{slotsToBlock.length > 0 && (
  <div className="mt-6 rounded-2xl bg-gray-200 p-4">
    <h3 className="text-base font-semibold text-gray-800">Slot Details</h3>

    <p className="text-sm text-gray-600 mt-1">
      {formatDate(slotsToBlock[0].date)}
    </p>

    <div className="mt-3 space-y-3">
      {slotsToBlock.map((s, idx) => (
        <div
          key={`${s.startTime}-${s.endTime}-${idx}`}
          className="bg-white rounded-2xl shadow-sm px-4 py-3 flex items-center justify-between"
        >
          <div>
            <p className="text-sm text-gray-600">
              {selectedCourtName} | Full Court
            </p>
            <p className="text-sm font-semibold tracking-wide">
              {formatTime(s.startTime)} - {formatTime(s.endTime)}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-base font-medium">
              ₹{Number(s.price || 0)}
            </span>
            <button
              type="button"
              onClick={() => removeSlotFromList(idx)}
              className="text-gray-500 hover:text-red-600 text-xl"
            >
              ×
            </button>
          </div>
        </div>
      ))}

      <div className="text-sm text-gray-600">
        Total: <span className="font-semibold">₹{totalAmount}</span>
      </div>
    </div>
  </div>
)}


            {/* Currently blocked list */}
        {/* Currently blocked list */}
<div className="mt-8">
  {blockedSlots.length === 0 ? (
    <p className="text-gray-200 text-sm">
      {/* No blocked slots found for this date. */}
    </p>
  ) : (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-800">
          Currently Blocked 
        </h3>

        <button
          type="button"
          onClick={handleUnblockAll}
          className="px-3 py-1 rounded-full border border-red-500 text-red-600 text-xs sm:text-sm hover:bg-red-50"
        >
          Unblock All
        </button>
      </div>
 <div className="mt-6 rounded-2xl bg-gray-200 p-4">
      {date && (
        <p className="text-sm text-gray-600 mb-3">
          {formatDate(date)}
        </p>
      )}

      <div className="space-y-3">
        {blockedSlots.map((b) => (
          <div
            key={b._id}
            className="bg-white shadow-sm rounded-2xl px-4 py-3 flex items-center justify-between"
          >
            <div>
              <p className="text-sm text-gray-600">
                {selectedCourtName} | Full Court
              </p>
              <p className="text-sm font-semibold tracking-wide">
                {formatTime(b.startTime)} - {formatTime(b.endTime)}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-base font-medium text-gray-800">
                ₹{b.price || 0}
              </span>

              <button
                type="button"
                onClick={() => handleUnblock(b._id)}
                className="text-gray-500 hover:text-red-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div> </div>
    </div>
  )}
</div>


            {/* Bottom buttons like screenshot */}
            {/* <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 h-9 rounded-xl border border-[#0f2250] text-[#0f2250] font-semibold"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={!slotsToBlock.length || !selectedVenue || !selectedCourt}
                className={`flex-1 h-9 rounded-xl font-semibold ${
                  slotsToBlock.length && selectedVenue && selectedCourt
                    ? "bg-[#0f2250] text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                BLOCK SLOT
              </button>
            </div> */}
            {/* FIXED Bottom Bar */}
<div className="
  fixed bottom-0 left-0 right-0
  bg-white border-t border-gray-300 px-4 py-3 
  lg:left-64   /* Move right on large screens */
">
  <div className="max-w-5xl mx-auto flex gap-3">
    
    <button
      type="button"
      onClick={() => navigate(-1)}
      className="flex-[1] sm:flex-1 h-9 rounded-xl border border-[#0f2250] text-[#0f2250] font-semibold"
    >
      CANCEL
    </button>

    <button
      type="submit"
      form="blockSlotForm"
      disabled={!slotsToBlock.length || !selectedVenue || !selectedCourt}
      className={`flex-[2] sm:flex-1 h-9 rounded-xl font-semibold ${
        slotsToBlock.length && selectedVenue && selectedCourt
          ? "bg-[#0f2250] text-white"
          : "bg-gray-300 text-gray-500 cursor-not-allowed"
      }`}
    >
      BLOCK SLOT
    </button>

  </div>
</div>



          </form>
        </div>
      </main>
    </div>
  );
}

export default BlockSlotsSingle;
